import { WebStorageStateStore } from "oidc-client-ts";
// The settings-based variant of react-oidc-context's provider props (not the
// pre-built-UserManager one): we build the config from env, so the return type
// must expose `authority`/`client_id`/`userStore`/… rather than the union, whose
// only common members would otherwise be inaccessible to callers.
import type { AuthProviderNoUserManagerProps as OidcProviderProps } from "react-oidc-context";

/**
 * OIDC configuration derived from SPA env vars.
 *
 * The public API of @repo/auth is deliberately NOT Keycloak-specific — it speaks plain OIDC
 * (auth-code + PKCE). Keycloak is just the issuer behind VITE_OIDC_ISSUER
 * (prod: https://auth.example.com/realms/template, local: http://localhost:8081/realms/template).
 * The realm ("template") and client id ("app") must match infra/keycloak-realm +
 * the realm-export.json imported by docker compose.
 */
export interface AuthConfig {
  issuer: string;
  clientId: string;
  redirectUri: string;
  scope: string;
}

export type AuthMode = "oidc" | "stub";

/**
 * Which auth implementation the app composes: real OIDC (default) or the dev-only stub that mints
 * HS256 tokens from the backend's `local-auth` profile (`POST /internal/local-auth/token`).
 *
 * Fail-closed: `stub` in a production build THROWS at startup — the escape hatch can never ship.
 * Anything other than the literal `"stub"` (unset, typos, …) falls back to real OIDC.
 */
export function readAuthMode(): AuthMode {
  const mode = import.meta.env.VITE_AUTH_MODE ?? "oidc";
  if (mode === "stub") {
    if (import.meta.env.PROD) {
      throw new Error(
        "VITE_AUTH_MODE=stub is a dev-only escape hatch and must never reach a production build",
      );
    }
    return "stub";
  }
  return "oidc";
}

export function readAuthConfig(): AuthConfig {
  const env = import.meta.env;
  return {
    issuer: env.VITE_OIDC_ISSUER ?? "",
    clientId: env.VITE_OIDC_CLIENT_ID ?? "",
    redirectUri: env.VITE_OIDC_REDIRECT_URI ?? globalThis.location?.origin ?? "",
    scope: env.VITE_OIDC_SCOPE ?? "openid profile email",
  };
}

export function toOidcProviderProps(config: AuthConfig): OidcProviderProps {
  return {
    authority: config.issuer,
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    // Land logout on the PUBLIC /logged-out page, NOT the app root. The root is behind RequireAuth,
    // and with the realm's straight-to-Google browser flow it would re-fire login() and silently
    // re-authenticate against the live Google session — making "log out" a no-op. `/logged-out`
    // stays signed out until the user chooses to sign in again. (Covered by the client's wildcard
    // valid-redirect-URIs, which post_logout_redirect_uris="+" reuses — no Keycloak change needed.)
    post_logout_redirect_uri: config.redirectUri
      ? new URL("/logged-out", config.redirectUri).toString()
      : config.redirectUri,
    response_type: "code",
    scope: config.scope,
    automaticSilentRenew: true,
    userStore:
      typeof window !== "undefined"
        ? new WebStorageStateStore({ store: window.localStorage })
        : undefined,
    // Strip the auth-code params from the URL after a successful callback.
    onSigninCallback: () => {
      window.history.replaceState({}, document.title, window.location.pathname);
    },
  };
}
